"use client";

import PropTypes from "prop-types";
import { useState, useEffect, useCallback } from "react";
import { twMerge } from "tailwind-merge";
import Cookies from "js-cookie";
import codeBlocks from "config/api/generated/code-blocks";
import { TAB_GROUPS } from "config/code-tabs";

const getGroupForTitle = (title) => {
  for (const [groupId, group] of Object.entries(TAB_GROUPS)) {
    if (group.options.includes(title)) return groupId;
  }
  return null;
};

const getGroupById = (groupId) => TAB_GROUPS[groupId];

export default function MDXCodeTabs({ children, initialPreferences }) {
  const childrenList = Array.isArray(children) ? children : [children];

  const tabs = childrenList.map((child) => {
    const codeData = child.props.children.props;
    const languageId = codeData?.["data-language"];
    const title = codeData?.["data-title"];

    return {
      language: languageId,
      title: title || codeBlocks[languageId] || languageId,
      theme: codeData?.["data-theme"],
      raw: codeData?.raw,
      group: getGroupForTitle(title || codeBlocks[languageId] || languageId),
    };
  });

  const getInitialActiveTabs = () => {
    const initialTabs = {};

    // Initialize all groups with first tab
    Object.keys(TAB_GROUPS).forEach((groupId) => {
      initialTabs[groupId] = 0;
    });

    // Make sure we do the safe for tabs that are not in a group
    initialTabs["default"] = 0;

    // Apply saved preferences
    Object.entries(initialPreferences || {}).forEach(
      ([groupId, preference]) => {
        if (preference) {
          const group = getGroupById(groupId);
          if (group && group.options.includes(preference)) {
            const matchingIndex = tabs.findIndex(
              (tab) => tab.title === preference && tab.group === groupId
            );
            if (matchingIndex !== -1) {
              initialTabs[groupId] = matchingIndex;
            }
          }
        }
      }
    );

    return initialTabs;
  };

  const [activeTabs, setActiveTabs] = useState(getInitialActiveTabs);

  const applyPreference = useCallback(
    (preferredTitle, groupId) => {
      if (tabs.length === 0) return false;

      const group = getGroupById(groupId);
      if (group && group.options.includes(preferredTitle)) {
        const matchingIndex = tabs.findIndex(
          (tab) => tab.title === preferredTitle && tab.group === groupId
        );

        if (matchingIndex !== -1) {
          setActiveTabs((prev) => ({ ...prev, [groupId]: matchingIndex }));
          Cookies.set(group.cookieName, preferredTitle, {
            expires: 365,
          });
          return true;
        }
      }
      return false;
    },
    [tabs]
  );

  useEffect(() => {
    const handleGlobalTabChange = (event) => {
      if (event.detail?.title && event.detail?.group) {
        applyPreference(event.detail.title, event.detail.group);
      }
    };
    window.addEventListener("code-tab-change", handleGlobalTabChange);
    return () =>
      window.removeEventListener("code-tab-change", handleGlobalTabChange);
  }, [applyPreference]);

  return (
    <div className="relative group is-code-tabs">
      <div className="absolute top-0 dark:top-[1px] inset-x-0 sm:right-auto z-10 overflow-hidden rounded-tl-lg rounded-tr-lg sm:rounded-tr-none">
        <div className="flex items-center font-mono overflow-x-scroll sm:overflow-x-auto dark:ml-[1px]">
          {tabs?.map((tab, index) => (
            <button
              key={index}
              type="button"
              className={twMerge(
                "flex items-center h-[41px] px-4 sm:px-6 text-sm",
                "first:rounded-tl-lg last:rounded-tr-lg last:sm:rounded-tr-none sm:border-r sm:border-gray-900 dark:sm:border-gray-200",
                (tab.group ? activeTabs[tab.group] : activeTabs["default"]) ===
                  index
                  ? "text-white bg-gray-900 dark:bg-gray-100 dark:text-gray-900"
                  : "text-gray-400 dark:text-gray-800 hover:text-white dark:hover:text-gray-900",
                "transition"
              )}
              onClick={() => {
                const groupId = tab.group || "default";
                setActiveTabs((prev) => ({ ...prev, [groupId]: index }));

                if (tab.title && tab.group) {
                  const group = getGroupById(tab.group);
                  if (group) {
                    Cookies.set(group.cookieName, tab.title, {
                      expires: 365,
                    });
                    window.dispatchEvent(
                      new CustomEvent("code-tab-change", {
                        detail: { title: tab.title, group: tab.group },
                      })
                    );
                  }
                }
              }}
            >
              {tab.title}
            </button>
          ))}
        </div>
      </div>

      <div className="relative">
        {childrenList.map((child, index) => {
          const tab = tabs[index];
          const isActive = tab?.group
            ? activeTabs[tab.group] === index
            : activeTabs["default"] === index;
          return (
            <div key={index} className={isActive ? "block" : "hidden"}>
              {child}
            </div>
          );
        })}
      </div>
    </div>
  );
}

MDXCodeTabs.propTypes = {
  children: PropTypes.node,
  initialPreferences: PropTypes.object,
};
